import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Stack, Button, TextField, Alert, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import api from '../../services/api';
import { toYyyyMmDd } from '../../utils/businessDays';
import StatusChip from '../StatusChip';
import { handleApiError, handleApiSuccess } from '../Common/ApiErrorHandler';

export default function SickLeaveCalendar() {
  const [sickLeaves, setSickLeaves] = useState([]);
  const [form, setForm] = useState({ startDate: '', endDate: '', notes: '' });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const leaves = await api.get('/leave-requests/sick-leaves-mine');
      setSickLeaves(Array.isArray(leaves) ? leaves : []);
    } catch (e) {
      console.warn('Failed to load sick leave data', e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const countDays = (startYmd, endYmd) => {
    if (!startYmd || !endYmd) return 0;
    const start = new Date(startYmd);
    const end = new Date(endYmd);
    if (end < start) return 0;
    let count = 0;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) count++;
    return count;
  };

  const validate = () => {
    if (!form.startDate) return 'Start date is required';
    if (!form.endDate) return 'End date is required';
    if (form.endDate < form.startDate) return 'End date cannot be before start date';
    // Certificate required only for new submissions, not for edits
    if (!editingId && !file) return 'Medical certificate is required';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('startDate', form.startDate);
      formData.append('endDate', form.endDate);
      formData.append('notes', form.notes || '');
      if (file) {
        formData.append('certificate', file);
      }

      if (editingId) {
        await api.put(`/leave-requests/sick-leave/${editingId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        handleApiSuccess('Sick leave updated successfully');
      } else {
        await api.post('/leave-requests/sick-leave', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        handleApiSuccess('Sick leave reported successfully');
      }
      resetForm();
      load();
    } catch (err) {
      handleApiError(err, editingId ? 'Failed to update sick leave' : 'Failed to submit sick leave');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ startDate: '', endDate: '', notes: '' });
    setFile(null);
    setError('');
    setEditingId(null);
  };

  const handleEdit = (sl) => {
    const startYmd = (sl.startDateLocalYmd || sl.StartDateLocalYmd) ?? toYyyyMmDd(new Date(sl.startDate || sl.StartDate));
    const endYmd = (sl.endDateLocalYmd || sl.EndDateLocalYmd) ?? toYyyyMmDd(new Date(sl.endDate || sl.EndDate));
    setForm({
      startDate: startYmd,
      endDate: endYmd,
      notes: sl.notes || sl.Notes || sl.reason || sl.Reason || ''
    });
    setFile(null);
    setEditingId(sl.id || sl.Id);
    setError('');
  };

  const days = countDays(form.startDate, form.endDate);

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>Sick Leave</Typography>

      {/* Submit Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {editingId ? 'Edit Sick Leave' : 'Report Sick Leave'}
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                value={form.startDate}
                onChange={(e) => setForm(f => ({ ...f, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
                size="small"
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="End Date"
                type="date"
                value={form.endDate}
                onChange={(e) => setForm(f => ({ ...f, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: form.startDate || undefined }}
                required
                size="small"
                sx={{ minWidth: 160 }}
              />
              {days > 0 && (
                <Typography variant="body1" sx={{ alignSelf: 'center', fontWeight: 500 }}>
                  {days} day{days !== 1 ? 's' : ''}
                </Typography>
              )}
            </Stack>

            <TextField
              label="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              size="small"
              multiline
              rows={2}
              fullWidth
            />

            <Stack direction="row" spacing={2} alignItems="center">
              <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />}>
                {file ? file.name : (editingId ? 'Upload New Certificate (optional)' : 'Upload Medical Certificate')}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>
              {file && (
                <IconButton size="small" onClick={() => setFile(null)} color="error">
                  <DeleteIcon />
                </IconButton>
              )}
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" spacing={2}>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || !form.startDate || !form.endDate || (!editingId && !file)}
              >
                {submitting ? 'Saving...' : (editingId ? 'Update Sick Leave' : 'Submit Sick Leave')}
              </Button>
              {editingId && (
                <Button variant="outlined" onClick={resetForm} disabled={submitting}>
                  Cancel
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {/* Previous Sick Leaves */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>My Sick Leaves</Typography>
        {sickLeaves.length === 0 ? (
          <Typography color="text.secondary">No sick leaves recorded</Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell align="right">Days</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Certificate</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sickLeaves.map(sl => {
                  const id = sl.id || sl.Id;
                  const startYmd = (sl.startDateLocalYmd || sl.StartDateLocalYmd) ?? toYyyyMmDd(new Date(sl.startDate || sl.StartDate));
                  const endYmd = (sl.endDateLocalYmd || sl.EndDateLocalYmd) ?? toYyyyMmDd(new Date(sl.endDate || sl.EndDate));
                  return (
                    <TableRow key={id} hover selected={editingId === id}>
                      <TableCell>{startYmd}</TableCell>
                      <TableCell>{endYmd}</TableCell>
                      <TableCell align="right">{sl.totalDays || sl.TotalDays || countDays(startYmd, endYmd)}</TableCell>
                      <TableCell>{sl.notes || sl.Notes || sl.reason || sl.Reason || '-'}</TableCell>
                      <TableCell><StatusChip status={sl.status || sl.Status || 'Approved'} /></TableCell>
                      <TableCell>
                        {(sl.medicalCertificateUrl || sl.MedicalCertificateUrl) ? (
                          <Button size="small" href={sl.medicalCertificateUrl || sl.MedicalCertificateUrl} target="_blank">
                            View
                          </Button>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleEdit(sl)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
